import React, { InputHTMLAttributes } from "react";
import { Form, InputGroup } from "react-bootstrap";
import classNames from "classnames";
import { FieldErrors, Control } from "react-hook-form";
import { useToggle } from "../../hooks";
import { ZLabel } from "components/ZLabel";

type PasswordInputProps = {
	name: string;
	placeholder?: string;
	refCallback?: any;
	errors: FieldErrors;
	control?: Control<any>;
	register?: any;
	className?: string;
};

/* Password Input */
const PasswordInput = ({ name, placeholder, refCallback, errors, register, className }: PasswordInputProps) => {
	const [showPassword, togglePassword] = useToggle();

	return (
		<InputGroup className="mb-0">
			<Form.Control
				type={showPassword ? "text" : "password"}
				placeholder={placeholder}
				name={name}
				id={name}
				as="input"
				ref={(r: HTMLInputElement) => {
					if (refCallback) refCallback(r);
				}}
				className={className}
				isInvalid={errors && errors[name] ? true : false}
				{...(register ? register(name) : {})}
				autoComplete={name}
			/>
			<div
				className={classNames("input-group-text", "input-group-password", {
					"show-password": showPassword,
				})}
				data-password={showPassword ? "true" : "false"}
			>
				<span className="password-eye" onClick={togglePassword}></span>
			</div>
		</InputGroup>
	);
};

type FormInputProps = InputHTMLAttributes<HTMLInputElement> & {
	label?: React.ReactNode;
	type?: string;
	name: string;
	placeholder?: string;
	register?: any;
	errors?: FieldErrors;
	control?: Control<any>;
	className?: string;
	labelClassName?: string;
	containerClass?: string;
	refCallback?: any;
	children?: React.ReactNode;
	rows?: string;
	prepend?: any;
	append?: any;
};

const FormInput = ({
	label,
	type,
	name,
	placeholder,
	register,
	errors,
	control,
	className,
	labelClassName,
	containerClass,
	refCallback,
	children,
	rows,
	prepend,
	append,
	...otherProps
}: FormInputProps) => {
	// handle input type
	const comp = type === "textarea" ? "textarea" : type === "select" ? "select" : "input";

	return (
		<>
			{type === "hidden" ? (
				<input type={type} name={name} {...(register ? register(name) : {})} {...otherProps} />
			) : (
				<>
					{type === "password" ? (
						<>
							<Form.Group className={containerClass}>
								{label ? (
									<>
										{" "}
										<ZLabel className={labelClassName}>{label}</ZLabel> {children}{" "}
									</>
								) : null}
								<PasswordInput name={name} placeholder={placeholder} refCallback={refCallback} errors={errors!} register={register} className={className} />

								{errors && errors[name] ? (
									<Form.Control.Feedback type="invalid" className="d-block">
										{/* {errors[name]["message"]} */}
									</Form.Control.Feedback>
								) : null}
							</Form.Group>
						</>
					) : (
						<>
							{type === "select" ? (
								<Form.Group className={containerClass}>
									{label ? <ZLabel className={labelClassName}>{label}</ZLabel> : null}

									<Form.Select
										type={type}
										label={label}
										name={name}
										id={name}
										ref={(r: HTMLInputElement) => {
											if (refCallback) refCallback(r);
										}}
										comp={comp}
										className={className}
										isInvalid={errors && errors[name] ? true : false}
										{...(register ? register(name) : {})}
										{...otherProps}
									>
										{children}
									</Form.Select>

									{errors && errors[name] ? <Form.Control.Feedback type="invalid">{/* {errors[name]["message"]} */}</Form.Control.Feedback> : null}
								</Form.Group>
							) : (
								<>
									{type === "checkbox" || type === "radio" ? (
										<Form.Group className={containerClass}>
											<Form.Check
												type={type}
												label={label}
												name={name}
												id={name}
												ref={(r: HTMLInputElement) => {
													if (refCallback) refCallback(r);
												}}
												className={className}
												isInvalid={errors && errors[name] ? true : false}
												{...(register ? register(name) : {})}
												{...otherProps}
											/>

											{errors && errors[name] ? <Form.Control.Feedback type="invalid">{/* {errors[name]["message"]} */}</Form.Control.Feedback> : null}
										</Form.Group>
									) : (
										<Form.Group className={containerClass}>
											{label ? <ZLabel className={labelClassName}>{label}</ZLabel> : null}

											<div className="input-group">
												{prepend && (
													<div className="input-group-prepend">
														<span className="input-group-text" style={{ padding: "0.2rem", borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>
															{prepend}
														</span>
													</div>
												)}

												<Form.Control
													type={type}
													placeholder={placeholder}
													name={name}
													id={name}
													as={comp}
													ref={(r: HTMLInputElement) => {
														if (refCallback) refCallback(r);
													}}
													className={className}
													isInvalid={errors && errors[name] ? true : false}
													onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
														otherProps.onChange && otherProps.onChange({ ...e, target: { ...e.target, value: e.target.value.trim() } });
														otherProps.onBlur && otherProps.onBlur(e);
													}}
													{...(register ? register(name) : {})}
													{...otherProps}
													autoComplete={"off"}
												>
													{children ? children : null}
												</Form.Control>

												{append && (
													<div className="input-group-append">
														<span className="input-group-text" style={{ padding: "0.2rem", borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
															{append}
														</span>
													</div>
												)}

												{errors && errors[name] ? <Form.Control.Feedback type="invalid">{/* {errors[name]["message"]} */}</Form.Control.Feedback> : null}
											</div>
										</Form.Group>
									)}
								</>
							)}
						</>
					)}
				</>
			)}
		</>
	);
};

export default FormInput;
